import { PropsWithChildren, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useWebchatRuntime } from '../../hooks/use-webchat-runtime'
import type { RootState } from '../../../store/redux-store'
import { hooks } from 'botframework-webchat'
import { faUndo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { EditConfirmation } from '../../components/edit-confirmation'
import { logInfoToDatadog, track } from '../../../common/utils'
import { colors, gaps } from '@ohgoodlord/design-system'
import { getConfirmationDescription, getConfirmationTitle } from './utils'

const { useSendEvent } = hooks

const StyledUserActivity = styled.div`
  display: block;
  position: relative;
`

const StyledEditActivityButton = styled.div`
  position: absolute;
  top: -15px;
  right: 35px;
  background-color: #f2f2f2;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s;

  &:hover {
    cursor: pointer;
    background-color: ${colors.redLight};
    color: ${colors.red};
  }
`

const StyledDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.xs};
`

interface ActivityDecoratorProps extends PropsWithChildren {
  activityId: string
}

const EditUserActivityDecorator = ({ activityId, children }: ActivityDecoratorProps) => {
  const [showModal, setShowModal] = useState(false)
  const sendEvent = useSendEvent()
  const { userActivityHistory } = useWebchatRuntime()
  const currentAction = useSelector((state: RootState) => state.webchat.currentAction)
  const enableEdit = useSelector((state: RootState) => state.webchat.enableEditAction)

  const userActivityAction = userActivityHistory.get(activityId)
  const isCurrentAction = userActivityAction?.actionId === currentAction?.actionId

  const handleOnClick = () => {
    setShowModal(true)
    logInfoToDatadog('Edit confirmation opened', {
      action: userActivityAction,
    })
    track('Click - Open edit confirmation', {
      action: userActivityAction,
    })
  }

  const handleOnSubmit = () => {
    sendEvent('restart.action', {
      action: userActivityAction,
    })
    setShowModal(false)
    logInfoToDatadog('Edit activity event sent', {
      action: userActivityAction,
    })
    track('Click - Edit bot action', {
      section: userActivityAction?.actionId,
      action: userActivityAction,
    })
  }

  if (enableEdit && isCurrentAction && userActivityAction) {
    return (
      <StyledUserActivity data-testid="user-activity">
        {children}
        <StyledEditActivityButton
          onClick={handleOnClick}
          data-testid="edit-user-activity-button"
        >
          <FontAwesomeIcon icon={faUndo} />
        </StyledEditActivityButton>
        {showModal && (
          <EditConfirmation
            title={getConfirmationTitle(userActivityAction.actionId) + '?'}
            description={
              <StyledDescription>
                {getConfirmationDescription(userActivityAction.actionId)}
              </StyledDescription>
            }
            confirmText={getConfirmationTitle(userActivityAction.actionId)}
            onSubmit={handleOnSubmit}
            onClose={() => {
              setShowModal(false)
              track('Click - Close edit confirmation', {
                action: userActivityAction,
              })
            }}
          />
        )}
      </StyledUserActivity>
    )
  }

  return <>{children}</>
}

export default EditUserActivityDecorator
